import axios, { AxiosError } from 'axios'
import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import { toast } from 'sonner'
import { refreshToken } from '@/utils/api/user-service'
import { clearAuthToken, getAuthToken, storeAuthToken, getAuthHeader } from '@/lib/session'

// Idle/session handling constants
const IDLE_TIMEOUT_MS = 15 * 60 * 1000 // 15 minutes
const GRACE_PERIOD_MS = 5 * 60 * 1000 // 5 minutes

let installed = false

// Idle management state
let idleTimer: number | undefined
let graceTimer: number | undefined
let warningOpen = false
const idleToastId = 'idle-warning'

// Token refresh coordination
let refreshInFlight: Promise<boolean> | null = null

function nowHasToken() {
  const { token, type } = getAuthToken()
  return !!token && !!type
}

// getAuthHeader comes from session helpers

// --- JWT helpers: decode exp from access token (no external deps) ---
function base64UrlDecode(input: string): string {
  // Replace URL-safe characters and add padding
  let b64 = input.replace(/-/g, '+').replace(/_/g, '/')
  const padding = 4 - (b64.length % 4)
  if (padding !== 4) b64 += '='.repeat(padding)
  try {
    return decodeURIComponent(
      Array.prototype.map
        .call(atob(b64), (c: string) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    )
  } catch {
    // Fallback
    return atob(b64)
  }
}

function getJwtExpirySeconds(token: string): number | null {
  const parts = token.split('.')
  if (parts.length < 2) return null
  try {
    const payloadJson = base64UrlDecode(parts[1])
    const payload = JSON.parse(payloadJson) as { exp?: number }
    return typeof payload.exp === 'number' ? payload.exp : null
  } catch {
    return null
  }
}

async function ensureFreshToken(minRemainingSeconds = 180): Promise<boolean> {
  const { token } = getAuthToken()
  if (!token) return false
  const exp = getJwtExpirySeconds(token)
  if (!exp) return true // can't parse; allow and let server decide
  const now = Math.floor(Date.now() / 1000)
  const remaining = exp - now
  if (remaining > minRemainingSeconds) return true

  // Need refresh; reuse in-flight if present
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await refreshToken()
        const data = res.data as { accessToken?: string; tokenType?: string }
        if (data?.accessToken && data?.tokenType) {
          const remembered = !!window.localStorage.getItem('auth_token')
          storeAuthToken(data.accessToken, data.tokenType, remembered)
          return true
        }
        return false
      } catch {
        return false
      } finally {
        setTimeout(() => {
          refreshInFlight = null
        }, 0)
      }
    })()
  }
  return await refreshInFlight
}

function scheduleIdleTimer() {
  // Only schedule if authenticated
  if (!nowHasToken()) return
  clearTimeout(idleTimer)
  idleTimer = window.setTimeout(onIdleThresholdReached, IDLE_TIMEOUT_MS)
}

function clearGrace() {
  if (graceTimer) {
    clearTimeout(graceTimer)
    graceTimer = undefined
  }
}

async function stayLoggedIn() {
  try {
    // Attempt token refresh
    const res = await refreshToken()
    const data = res.data as { accessToken?: string; tokenType?: string }
    if (data?.accessToken && data?.tokenType) {
      // Preserve rememberMe by detecting existing storage location
      const remembered = !!window.localStorage.getItem('auth_token')
      storeAuthToken(data.accessToken, data.tokenType, remembered)
      toast.success('Session extended')
      resetActivity()
      return true
    }
    return false
  } catch (err) {
    return false
  }
}

function forceLogout(reason?: 'idle' | 'unauthorized') {
  clearGrace()
  warningOpen = false
  try {
    toast.dismiss(idleToastId)
  } catch {}
  clearAuthToken()
  // Redirect to login with return path
  const loc = window.location
  const redirect = encodeURIComponent(loc.pathname + loc.search)
  const msg = reason === 'idle' ? 'Session expired due to inactivity' : undefined
  const target = `/login?redirect=${redirect}` + (msg ? `&reason=${encodeURIComponent(msg)}` : '')
  window.location.href = target
}

function onIdleThresholdReached() {
  if (warningOpen) return
  warningOpen = true
  // Show a persistent toast with actions
  toast(
    'You’re going idle',
    {
      id: idleToastId,
      description: 'You will be logged out in 5 minutes unless you stay logged in.',
      duration: GRACE_PERIOD_MS,
      action: {
        label: 'Stay logged in',
        onClick: async () => {
          clearGrace()
          const ok = await stayLoggedIn()
          if (!ok) forceLogout('idle')
        },
      },
      cancel: {
        label: 'Log out now',
        onClick: () => forceLogout('idle'),
      },
    },
  )

  // Start grace countdown; any user activity during grace renews session
  clearGrace()
  graceTimer = window.setTimeout(() => {
    // If still open and no action, logout
    if (warningOpen) forceLogout('idle')
  }, GRACE_PERIOD_MS)
}

function resetActivity() {
  // Any activity cancels warning and reschedules idle timer
  if (!nowHasToken()) return // do nothing if logged out
  warningOpen = false
  try {
    toast.dismiss(idleToastId)
  } catch {}
  clearGrace()
  scheduleIdleTimer()
}

function installActivityListeners() {
  const events: Array<keyof DocumentEventMap> = [
    'mousemove',
    'mousedown',
    'keydown',
    'scroll',
    'touchstart',
    'visibilitychange',
  ]
  const handler = () => {
    // While the idle warning is open, ignore incidental activity (e.g., mouse hover)
    // The dialog should only close when user clicks an action button.
    if (warningOpen) {
      return
    }
    // Otherwise, any activity resets the idle timer normally
    scheduleIdleTimer()
  }
  events.forEach((evt) => document.addEventListener(evt, handler, { passive: true }))
}

function setup401Interceptor() {
  // First, in development, rewrite absolute UIBFS API URLs to the Vite proxy path to avoid CORS
  axios.interceptors.request.use((config) => {
    if (typeof window !== 'undefined' && (import.meta as any).env?.DEV) {
      const rawUrl = (config.url || '').toString()
      // Match any absolute URL to *.uibfs.or.ug that uses the /uib-api prefix and rewrite to relative
      const m = rawUrl.match(/^https?:\/\/[^/]*uibfs\.or\.ug(\/uib-api\/?[^?#]*)/i)
      if (m) {
        const relativePath = m[1]
        config.url = relativePath.startsWith('/') ? relativePath : `/${relativePath}`
        // Ensure no baseURL interferes when we force a relative path
        if (config.baseURL) config.baseURL = undefined
      }
    }
    return config
  })

  // Pre-request: proactively refresh if token is expired or expiring soon
  axios.interceptors.request.use(async (config) => {
    const url = (config.url || '').toString()
    // Skip for auth endpoints
    if (
      url.includes('/auth/refresh') ||
      url.includes('/auth/sign-in') ||
      url.includes('/auth/sign-up') ||
      url.includes('/auth/request-password-reset') ||
      url.includes('/auth/password-reset')
    ) {
      return config
    }

    // If no token present (unauthenticated flows like signup), allow request without forcing logout
    const existingAuthHeader = getAuthHeader()
    if (!existingAuthHeader) {
      return config
    }

    // If we have a token and it's expiring within 3 minutes, refresh first
    const ok = await ensureFreshToken(180)
    if (!ok) {
      // If we failed to ensure a fresh token, force logout and cancel the request
      forceLogout('unauthorized')
      return Promise.reject(new Error('Unauthorized: token refresh failed'))
    }

    // Ensure Authorization header is up to date for this request
    const authHeader = getAuthHeader()
    if (authHeader) {
      const hdrs: any = config.headers
      if (hdrs && typeof hdrs.set === 'function') {
        hdrs.set('Authorization', authHeader)
      } else {
        config.headers = { ...(config.headers as any), Authorization: authHeader } as any
      }
    }
    return config
  })

  axios.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const status = error.response?.status
      const config = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined
      const failingUrl = (config?.url || '').toString()

      if (status === 401 || status === 403) {
        // If the refresh endpoint itself failed, force logout
        if (failingUrl.includes('/auth/refresh')) {
          forceLogout('unauthorized')
          return Promise.reject(error)
        }
        // If the sign-in endpoint returned 401 (bad credentials), let the login
        // form's own catch block handle it — do NOT force logout / redirect.
        if (failingUrl.includes('/auth/sign-in')) {
          return Promise.reject(error)
        }
        // Avoid loops
        if (config && config._retry) {
          forceLogout('unauthorized')
          return Promise.reject(error)
        }

        // Attempt a single refresh for all concurrent 401s
        if (!refreshInFlight) {
          refreshInFlight = (async () => {
            try {
              const res = await refreshToken()
              const data = res.data as { accessToken?: string; tokenType?: string }
              if (data?.accessToken && data?.tokenType) {
                const remembered = !!window.localStorage.getItem('auth_token')
                storeAuthToken(data.accessToken, data.tokenType, remembered)
                return true
              }
              return false
            } catch {
              return false
            } finally {
              // Allow subsequent refresh attempts later
              setTimeout(() => { refreshInFlight = null }, 0)
            }
          })()
        }

        const ok = await refreshInFlight
        if (ok && config) {
          // Retry original request with updated Authorization header
          config._retry = true
          const authHeader = getAuthHeader()
          if (authHeader) {
            const hdrs: any = config.headers
            if (hdrs && typeof hdrs.set === 'function') {
              hdrs.set('Authorization', authHeader)
            } else {
              config.headers = { ...(config.headers as any), Authorization: authHeader } as any
            }
          }
          return axios(config)
        }

        // Refresh failed: logout
        forceLogout('unauthorized')
      }

      return Promise.reject(error)
    },
  )
}

export function installHttpInterceptors() {
  if (installed) return
  installed = true

  // Prime Authorization header on boot if token exists
  const hdr = getAuthHeader()
  if (hdr) axios.defaults.headers.common['Authorization'] = hdr
  // Ensure refresh-token cookies are sent
  axios.defaults.withCredentials = true

  setup401Interceptor()
  installActivityListeners()
  scheduleIdleTimer()
}

export default installHttpInterceptors
